import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useRef } from 'react';
import FormUpload from '../../../components/formInputs/FormUpload';
import { getMedia, scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IVehicleMakeForm } from '../types/interfaces';
import { customStyles } from '../../../utils/SelectStyles';
import SwitchActivation from '../../../components/layout/SwitchActivation';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';

const CreateVehicleMakeForm = () => {
  const { id } = useParams();
  const formRef = useRef(null);
  const { t } = useTranslation();

  const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    queryKey: ['vehicle-makes', id],
    invalidateQueryKey: ['vehicle-makes'],
    id,
    singleGetApi: `/admin/makes/${id}`,
    addApi: '/admin/makes',
    editApi: `/admin/makes/${id}`,
    itemName: t('vehicle_make'),
    listRoute: '/vehicle-makes'
  });

  const validationSchema = Yup.object({
    name: Yup.string().required(t('required')).min(3, t('name_short')),
    slug: Yup.string().required(t('required')),
    code: Yup.string().required(t('required')),
    explore_button: Yup.string().required(t('required'))
  });

  const initialValues: IVehicleMakeForm = {
    ...apiData,
    
    name: apiData?.name || '',
    icon: apiData?.icon || '',
    slug: apiData?.slug || '',
    code: apiData?.code || '',
    explore_button: apiData?.explore_button || '',
    primary_color_hex: apiData?.primary_color_hex || '',
    secondary_color_hex: apiData?.secondary_color_hex || '',
    
    hidden: apiData?.hidden !== undefined ? apiData.hidden : 0,
    is_service_booking_enabled: apiData?.is_service_booking_enabled !== undefined ? apiData.is_service_booking_enabled : 0,
    is_location_checkout_enabled: apiData?.is_location_checkout_enabled !== undefined ? apiData.is_location_checkout_enabled : 0,
    
    logo_black: getMedia(apiData?.media, 'logo_black')?.url || '',
    logo_white: getMedia(apiData?.media, 'logo_white')?.url || '',
    logo: getMedia(apiData?.media, 'logo')?.url || ''
  };

  const exploreButtonOptions = [
    { label: t('call-us'), value: 'call_us' },
    { label: t('request-a-quote'), value: 'request_quote' }
  ];

  if (getDataLoading || !initialValues)
    return (
      <FormDynamicSkeleton
        sections={[
          [
            { columns: 4, items: 3, type: 'textarea' },
          ],
          [
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
          ],
          [
            { columns: 1, items: 1, type: 'switch' },
            { columns: 1, items: 1, type: 'switch' },
            { columns: 1, items: 1, type: 'switch' },
          ]
        ]}
        buttons={1}
      />
    );

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setErrors }) => {
          let value = { ...values, _method: 'POST' };
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          handleSubmit({ ...value }, setErrors);
        }}
      >
        {(formik) => (
          <>
            <Form>
              <SectionHeader title={t('logo')}>
                <div className="inputs_group">
                  <FieldWrapper>
                    <FormUpload errors={formik?.errors.logo as string} touched={formik?.touched?.logo as boolean} formik={formik} name={`logo`} label={t('colored_logo')} />
                  </FieldWrapper>
                  <FieldWrapper>
                    <FormUpload
                      errors={formik?.errors.logo_white as string}
                      touched={formik?.touched?.logo_white as boolean}
                      formik={formik}
                      name={`logo_white`}
                      label={t('white_logo')}
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <FormUpload
                      errors={formik?.errors.logo_black as string}
                      touched={formik?.touched?.logo_black as boolean}
                      formik={formik}
                      name={`logo_black`}
                      label={t('black_logo')}
                    />
                  </FieldWrapper>
                  <FieldWrapper noPadding />
                </div>
              </SectionHeader>
              <SectionHeader title={t('vehicle_make_details')}>
                <div className="inputs_group">
                  <FieldWrapper title={t('name')} inputName={`name`} inputPlaceholder={t('name')} input customPadding />
                  <FieldWrapper title={t('slug')} inputName={`slug`} inputPlaceholder={t('slug')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('code')} inputName={`code`} inputPlaceholder={t('code')} input customPadding />
                  <FieldWrapper
                    customPadding
                    title={t('explore_button')}
                    inputPlaceholder={t('explore_button')}
                    inputName={`explore_button`}
                    options={exploreButtonOptions}
                    selectStyle={customStyles}
                    select
                  />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('primary_hex')} inputName={`primary_color_hex`} inputPlaceholder={t('primary_hex')} input customPadding />
                  <FieldWrapper title={t('secondary_hex')} inputName={`secondary_color_hex`} inputPlaceholder={t('secondary_hex')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('hotline')} inputName={`hotline_number`} inputPlaceholder={t('hotline')} input customPadding />
                  <FieldWrapper title={t('whatsapp_number')} inputName={`whats_app_number`} inputPlaceholder={t('whatsapp_number')} input customPadding />
                </div>
              </SectionHeader>
              <SectionHeader title={t('advanced_settings')} customStyle="last">
                <div className="switch_container">
                  <SwitchActivation onChange={(value: any) => formik.setFieldValue('hidden', value?.target?.checked ? 1 : 0)} active={formik.values.hidden} />
                  <p>
                    {t('hide_from_website')} <span className="side_note">({t('active_means_hide')})</span>
                  </p>
                </div>
                <div className="switch_container">
                  <SwitchActivation
                    onChange={(value: any) => formik.setFieldValue('is_service_booking_enabled', value?.target?.checked ? 1 : 0)}
                    active={formik.values.is_service_booking_enabled}
                  />
                  <p>
                    {t('disable_service_booking')} <span className="side_note">({t('active_means_disabled')})</span>
                  </p>
                </div>
                <div className="switch_container">
                  <SwitchActivation
                    onChange={(value: any) => formik.setFieldValue('is_location_checkout_enabled', value?.target?.checked ? 1 : 0)}
                    active={formik.values.is_location_checkout_enabled}
                  />
                  <p>
                    {t('disable_locations')} <span className="side_note">({t('active_means_disabled')})</span>
                  </p>
                </div>
              </SectionHeader>
              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('save')}</span>
                </Button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateVehicleMakeForm;
